const setupSearchInput = (searchInput: HTMLInputElement): (() => void) => {
  const clearIcon = searchInput.nextElementSibling as HTMLElement

  const currentVal = searchInput.value

  if (!currentVal) {
    clearIcon.style.opacity = '0'
  }

  searchInput.addEventListener('keyup', handleKeyUp)
  clearIcon.addEventListener('click', clearSearch)

  function handleKeyUp(event: KeyboardEvent): void {
    const target = event.currentTarget as HTMLInputElement

    if (target.value) {
      clearIcon.style.opacity = '1'
    } else {
      clearIcon.style.opacity = '0'
    }
  }

  function clearSearch(): void {
    searchInput.value = ''
    clearIcon.style.opacity = '0'

    // Dispatch a fake keyUp event to notify any keyup listeners
    searchInput.dispatchEvent(new KeyboardEvent('keyup'))
  }

  return () => {
    searchInput.removeEventListener('keyup', handleKeyUp)
    clearIcon.removeEventListener('click', clearSearch)
  }
}

// Teardown is navigation-scoped: inputs wired for a turbo frame that has since
// reloaded stay in `unsubscribe` (harmlessly, on detached nodes) until the next
// turbolinks:visit clears the whole array.
export default (): (() => void) => {
  const unsubscribe: Array<() => void> = []

  const setupSearchInputs = (root: ParentNode): void => {
    root
      .querySelectorAll('[data-behaviour-search="true"]')
      .forEach((searchInput) => {
        unsubscribe.push(setupSearchInput(searchInput as HTMLInputElement))
      })
  }

  const teardown = (): void => {
    unsubscribe.forEach((fn) => fn())
    unsubscribe.length = 0
  }

  // onDomLoad runs the callback on turbolinks:load (scoped to the document) and
  // on turbo:frame-load (scoped to the loaded frame), so inputs inside lazily-
  // loaded turbo frames get wired up when their content arrives — without
  // re-querying (and re-binding) the whole document on each frame load.
  window.onDomLoad(($dom) => {
    setupSearchInputs($dom[0])
  })

  // Registered once at boot so teardown listeners don't accumulate across frame
  // loads. Emptying the array lets a restored page rewire from scratch.
  document.addEventListener('turbolinks:visit', teardown)

  return () => {
    document.removeEventListener('turbolinks:visit', teardown)
    teardown()
  }
}
